/**
 * Routen der Anwendung.
 *
 * Ohne Anmeldung erscheint die Anmeldemaske; steht ein erzwungener
 * Passwortwechsel an, kommt der Benutzer zunaechst nur dorthin.
 */

import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from './auth';
import { Layout } from './components/Layout';
import { LoadingBlock } from './components/ui';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AuditPage } from './pages/AuditPage';
import { BankStatementsPage } from './pages/BankStatementsPage';
import { ComplaintsPage } from './pages/ComplaintsPage';
import { CrmActivitiesPage } from './pages/CrmActivitiesPage';
import { CrmFollowUpsPage } from './pages/CrmFollowUpsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DatevPage } from './pages/DatevPage';
import { JournalPage } from './pages/JournalPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { DocumentRedirect } from './pages/DocumentRedirect';
import { VorgangPage } from './pages/VorgangPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { EInvoiceImportPage } from './pages/EInvoiceImportPage';
import { ReorderPage } from './pages/ReorderPage';
import { ReportsPage } from './pages/ReportsPage';
import { RepairsPage } from './pages/RepairsPage';
import { StockPage } from './pages/StockPage';
import { ItemDetailPage } from './pages/ItemDetailPage';
import { ItemsPage } from './pages/ItemsPage';
import { LoginPage, PasswordChangePage } from './pages/LoginPage';
import { PartnerDetailPage } from './pages/PartnerDetailPage';
import { PartnersPage } from './pages/PartnersPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';

export function App() {
  const { user, loading, moduleEnabled } = useAuth();

  if (loading) {
    return (
      <div className="login-screen">
        <LoadingBlock text="Anwendung wird geladen …" />
      </div>
    );
  }

  if (!user) return <LoginPage />;
  if (user.mustChangePassword) return <PasswordChangePage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/auswertungen" element={<AnalyticsPage />} />
        <Route path="/auswertungen/berichte" element={<ReportsPage />} />

        <Route path="/kunden" element={<PartnersPage role="customer" />} />
        <Route path="/kunden/:id" element={<PartnerDetailPage role="customer" />} />
        <Route path="/lieferanten" element={<PartnersPage role="supplier" />} />
        <Route path="/lieferanten/:id" element={<PartnerDetailPage role="supplier" />} />

        <Route path="/artikel" element={<ItemsPage />} />
        <Route path="/artikel/sets" element={<ItemsPage kind="set" />} />
        <Route path="/artikel/:id" element={<ItemDetailPage />} />

        <Route path="/belege/:id" element={<DocumentRedirect />} />
        <Route path="/vorgang/:id" element={<VorgangPage />} />
        <Route path="/verkauf/angebote" element={<DocumentsPage type="quote" />} />
        <Route path="/verkauf/angebote/:id" element={<DocumentDetailPage type="quote" />} />
        <Route path="/verkauf/auftraege" element={<DocumentsPage type="sales_order" />} />
        <Route path="/verkauf/auftraege/:id" element={<DocumentDetailPage type="sales_order" />} />
        <Route path="/verkauf/rahmenvertraege" element={<DocumentsPage type="sales_order" framework />} />
        <Route
          path="/verkauf/rahmenvertraege/:id"
          element={<DocumentDetailPage type="sales_order" framework />}
        />
        <Route path="/verkauf/lieferscheine" element={<DocumentsPage type="delivery_note" />} />
        <Route
          path="/verkauf/lieferscheine/:id"
          element={<DocumentDetailPage type="delivery_note" />}
        />
        <Route path="/verkauf/rechnungen" element={<DocumentsPage type="invoice" />} />
        <Route path="/verkauf/rechnungen/:id" element={<DocumentDetailPage type="invoice" />} />
        <Route path="/verkauf/gutschriften" element={<DocumentsPage type="credit_note" />} />
        <Route path="/verkauf/gutschriften/:id" element={<DocumentDetailPage type="credit_note" />} />

        {moduleEnabled('crm') ? (
          <>
            <Route path="/crm/wiedervorlagen" element={<CrmFollowUpsPage />} />
            <Route path="/crm/aktivitaeten" element={<CrmActivitiesPage />} />
          </>
        ) : null}
        {moduleEnabled('complaints') ? (
          <Route path="/reklamationen" element={<ComplaintsPage />} />
        ) : null}
        {moduleEnabled('repairs') ? (
          <Route path="/werkstatt/reparaturen" element={<RepairsPage />} />
        ) : null}

        <Route path="/einkauf/bestellungen" element={<DocumentsPage type="purchase_order" />} />
        <Route
          path="/einkauf/bestellungen/:id"
          element={<DocumentDetailPage type="purchase_order" />}
        />
        <Route path="/einkauf/wareneingang" element={<DocumentsPage type="goods_receipt" />} />
        <Route
          path="/einkauf/wareneingang/:id"
          element={<DocumentDetailPage type="goods_receipt" />}
        />
        <Route
          path="/einkauf/eingangsrechnungen"
          element={<DocumentsPage type="purchase_invoice" />}
        />
        <Route
          path="/einkauf/eingangsrechnungen/:id"
          element={<DocumentDetailPage type="purchase_invoice" />}
        />
        <Route path="/einkauf/erechnung-import" element={<EInvoiceImportPage />} />
        <Route path="/einkauf/bestellvorschlag" element={<ReorderPage />} />
        <Route path="/lager/bestand" element={<StockPage />} />

        <Route path="/buchhaltung/journal" element={<JournalPage />} />
        <Route path="/buchhaltung/zahlungen" element={<PaymentsPage />} />
        <Route path="/buchhaltung/kontoauszug" element={<BankStatementsPage />} />
        <Route path="/buchhaltung/datev" element={<DatevPage />} />

        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route path="/benutzer" element={<UsersPage />} />
        <Route path="/protokoll" element={<AuditPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
